import { AlertTriangle, BarChart3, CheckCircle, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

interface PolicyMetrics {
	total_policies: number;
	active_policies: number;
	policy_violations: number;
	successful_enforcements: number;
	recent_activity: PolicyActivity[];
}

interface PolicyActivity {
	id: string;
	policy_name: string;
	action: "allow" | "deny" | "require_auth";
	resource: string;
	timestamp: string;
	user?: string;
}

export function PolicyAnalytics() {
	const [metrics, setMetrics] = useState<PolicyMetrics | null>(null);

	useEffect(() => {
		// Simulate loading policy metrics
		setTimeout(() => {
			setMetrics({
				total_policies: 12,
				active_policies: 9,
				policy_violations: 23,
				successful_enforcements: 1247,
				recent_activity: [
					{
						id: "1",
						policy_name: "Require Authentication for Private Packages",
						action: "require_auth",
						resource: "private-package@1.0.0",
						timestamp: "2024-01-15T14:30:00Z",
						user: "john.doe",
					},
					{
						id: "2",
						policy_name: "Block Large Package Uploads",
						action: "deny",
						resource: "huge-package@2.0.0",
						timestamp: "2024-01-15T14:25:00Z",
						user: "jane.smith",
					},
					{
						id: "3",
						policy_name: "Allow Public Package Access",
						action: "allow",
						resource: "public-lib@1.5.0",
						timestamp: "2024-01-15T14:20:00Z",
					},
				],
			});
		}, 300);
	}, []);

	if (!metrics) {
		return (
			<Card>
				<CardContent className="p-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4"></div>
						<div className="h-20 bg-gray-200 rounded"></div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const getActionIcon = (action: string) => {
		switch (action) {
			case "allow":
				return <CheckCircle className="w-4 h-4 text-green-500" />;
			case "deny":
				return <AlertTriangle className="w-4 h-4 text-red-500" />;
			case "require_auth":
				return <Shield className="w-4 h-4 text-blue-500" />;
			default:
				return <BarChart3 className="w-4 h-4 text-gray-500" />;
		}
	};

	const getActionColor = (action: string) => {
		switch (action) {
			case "allow":
				return "bg-green-100 text-green-800";
			case "deny":
				return "bg-red-100 text-red-800";
			case "require_auth":
				return "bg-blue-100 text-blue-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	return (
		<div className="space-y-6">
			{/* Metrics Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total Policies
						</CardTitle>
						<Shield className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{metrics.total_policies}</div>
						<p className="text-xs text-muted-foreground">
							{metrics.active_policies} active
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Active Policies
						</CardTitle>
						<CheckCircle className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{metrics.active_policies}
						</div>
						<p className="text-xs text-muted-foreground">Enforcing rules</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Violations</CardTitle>
						<AlertTriangle className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-red-600">
							{metrics.policy_violations}
						</div>
						<p className="text-xs text-muted-foreground">Last 7 days</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Enforcements</CardTitle>
						<BarChart3 className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{metrics.successful_enforcements}
						</div>
						<p className="text-xs text-muted-foreground">Total actions taken</p>
					</CardContent>
				</Card>
			</div>

			{/* Recent Activity */}
			<Card>
				<CardHeader>
					<CardTitle>Recent Policy Activity</CardTitle>
					<CardDescription>
						Latest policy enforcements and violations
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-3">
						{metrics.recent_activity.map((activity) => (
							<div
								key={activity.id}
								className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
							>
								<div className="flex items-center gap-3">
									{getActionIcon(activity.action)}
									<div>
										<p className="text-sm font-medium text-gray-900">
											{activity.policy_name}
										</p>
										<p className="text-xs text-gray-500">
											{activity.action} for {activity.resource}
											{activity.user && ` by ${activity.user}`}
										</p>
									</div>
								</div>
								<div className="flex items-center gap-2">
									<span
										className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}
									>
										{activity.action}
									</span>
									<span className="text-xs text-gray-500">
										{new Date(activity.timestamp).toLocaleString()}
									</span>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
